import { z } from 'zod';

/**
 * The words the sales side of the book is recorded in — why a deal was lost, where a follow-up
 * task has got to, which way a call went and how it ended — under names that belong to no one
 * kind of record.
 *
 * Depends on nothing but zod on purpose: `deals.ts`, `tasks.ts` and `customer-follow-ups.ts` all
 * read it, and a constant shared through an import cycle is a constant that is `undefined` for
 * whichever module loads first.
 */

/**
 * Why a deal was lost. Closed rather than free text, because the only useful version of this
 * field is one a report can group — "we lose on price" is an answer a shop can act on.
 */
export const DEAL_LOST_REASONS = [
  'price',
  'timeline',
  'went_elsewhere',
  'no_response',
  'not_serious',
  'other',
] as const;
export type DealLostReason = (typeof DEAL_LOST_REASONS)[number];
export const dealLostReasonSchema = z.enum(DEAL_LOST_REASONS);

/**
 * Where a follow-up task has got to. THREE, and the count is load-bearing: `open` is the only
 * status the follow-up date is derived from, and `cancelled` exists so that "they asked us to
 * stop chasing" is not recorded as `done`. See `tasks.ts` for the full rationale.
 */
export const TASK_STATUSES = ['open', 'done', 'cancelled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const taskStatusSchema = z.enum(TASK_STATUSES);

/** The statuses that no longer chase. */
export const CLOSED_TASK_STATUSES = ['done', 'cancelled'] as const;

export function isOpenTask(status: TaskStatus): boolean {
  return status === 'open';
}

/** Two, not five: what a shop needs to say is "this one before the others". */
export const TASK_PRIORITIES = ['normal', 'high'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

/** Who dialled. Kept apart from the outcome, so a missed INBOUND call can be said at all. */
export const CALL_DIRECTIONS = ['outbound', 'inbound'] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];
export const callDirectionSchema = z.enum(CALL_DIRECTIONS);

/**
 * How a call went. Closed rather than free text, because "we ring them and they never pick up"
 * is a pattern worth seeing, and it is only visible if the outcomes group.
 */
export const CALL_OUTCOMES = ['reached', 'no_answer', 'busy', 'wrong_number', 'not_now'] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];
export const callOutcomeSchema = z.enum(CALL_OUTCOMES);
